import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { FiEdit2, FiPlus, FiToggleLeft, FiToggleRight, FiSearch, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import '../styles/Maestros.css';

const TIPOS_DOC_FALLBACK = [
  { id: 'DNI', name: 'DNI', short_name: 'DNI' },
  { id: 'RUC', name: 'RUC', short_name: 'RUC' },
  { id: 'CE',  name: 'CARNET DE EXTRANJERÍA', short_name: 'CE' },
];

const EMPTY = {
  tipo_documento: 'DNI', nro_documento: '', nombres: '',
  apellido_paterno: '', apellido_materno: '',
  telefono: '', email: '', direccion: '',
  representante: '', limite_credito: 0,
};

export default function ClientesPage() {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState('');
  const [confirmDel, setConfirmDel] = useState(null); // id en espera de confirmación
  const [tiposDoc,   setTiposDoc]   = useState(TIPOS_DOC_FALLBACK);

  const load = useCallback(async (q = '') => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/clientes', { params: { q } });
      setRows(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/api/config/tipos-documento')
      .then(r => {
        const list = r.data?.data || [];
        if (list.length) setTiposDoc(list);
      })
      .catch(() => {});
  }, []);

  // Auto-genera representante cuando no es RUC
  useEffect(() => {
    if (form.tipo_documento !== 'RUC') {
      const partes = [
        form.apellido_paterno.trim(),
        form.apellido_materno.trim(),
        form.nombres.trim(),
      ].filter(Boolean);
      setForm(p => ({ ...p, representante: partes.join(' ') }));
    }
  }, [form.nombres, form.apellido_paterno, form.apellido_materno, form.tipo_documento]);

  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    load(v);
  };

  const openNew = () => {
    setForm(EMPTY);
    setModalError('');
    setModal({ mode: 'new' });
  };

  const openEdit = (r) => {
    setForm({
      tipo_documento:  r.tipo_documento  || 'DNI',
      nro_documento:   r.nro_documento   || '',
      nombres:         r.nombres         || '',
      apellido_paterno: r.apellido_paterno || '',
      apellido_materno: r.apellido_materno || '',
      telefono:        r.telefono        || '',
      email:           r.email           || '',
      direccion:       r.direccion       || '',
      representante:   r.representante   || '',
      limite_credito:  r.limite_credito  || 0,
    });
    setModalError('');
    setModal({ mode: 'edit', id: r.id });
  };

  const handleChange = (field, val) => {
    setModalError('');
    setForm(p => ({ ...p, [field]: val }));
  };

  const LETTERS = /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s'-]+$/;
  const PHONE   = /^[\d\s+\-()\\.]{6,20}$/;

  const validate = () => {
    if (!form.nombres.trim())
      return 'Nombres es requerido.';
    if (!LETTERS.test(form.nombres.trim()))
      return 'Nombres solo debe contener letras.';
    if (form.apellido_paterno.trim() && !LETTERS.test(form.apellido_paterno.trim()))
      return 'Apellido paterno solo debe contener letras.';
    if (form.apellido_materno.trim() && !LETTERS.test(form.apellido_materno.trim()))
      return 'Apellido materno solo debe contener letras.';

    const doc = form.nro_documento.trim();
    if (doc) {
      if (form.tipo_documento === 'DNI' && !/^\d{8}$/.test(doc))
        return 'DNI debe tener exactamente 8 dígitos numéricos.';
      if (form.tipo_documento === 'RUC' && !/^\d{11}$/.test(doc))
        return 'RUC debe tener exactamente 11 dígitos numéricos.';
      if (form.tipo_documento === 'CE' && (doc.length < 4 || doc.length > 12))
        return 'Carnet de Extranjería debe tener entre 4 y 12 caracteres.';
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return 'Formato de email inválido.';
    if (form.telefono.trim() && !PHONE.test(form.telefono.trim()))
      return 'Teléfono inválido. Use dígitos, +, -, espacios (6-20 caracteres).';
    if (parseFloat(form.limite_credito) < 0)
      return 'Límite de crédito no puede ser negativo.';

    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setModalError(err); return; }
    setSaving(true); setModalError('');
    try {
      if (modal.mode === 'new') {
        await api.post('/api/clientes', form);
      } else {
        await api.put(`/api/clientes/${modal.id}`, form);
      }
      await load(search);
      setModal(null);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/api/clientes/${id}/activo`);
      await load(search);
    } catch (e) {
      showToast(e.response?.data?.error || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); return; }
    setConfirmDel(null);
    try {
      await api.delete(`/api/clientes/${id}`);
      showToast('Cliente eliminado');
      await load(search);
    } catch (e) {
      showToast(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <div className="mst-page" onClick={() => setConfirmDel(null)}>
      {/* Toolbar */}
      <div className="mst-toolbar">
        <button className="mst-btn-nuevo" onClick={openNew}>
          <FiPlus size={14} /> Nuevo Cliente
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiSearch size={13} style={{ color: '#94a3b8' }} />
          <input
            className="mst-search"
            placeholder="Buscar por nombre, documento, teléfono..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <span className="mst-count">{rows.length} registro(s)</span>
      </div>

      {/* Tabla */}
      <div className="mst-table-wrap">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>Cargando...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#dc2626' }}>{error}</div>
        ) : (
          <table className="mst-table">
            <thead>
              <tr>
                <th className="mst-th" style={{ width: 80 }}>Tipo Doc.</th>
                <th className="mst-th" style={{ width: 110 }}>N° Documento</th>
                <th className="mst-th">Apellidos y Nombres</th>
                <th className="mst-th" style={{ width: 120 }}>Teléfono</th>
                <th className="mst-th" style={{ width: 170 }}>Email</th>
                <th className="mst-th">Dirección</th>
                <th className="mst-th mst-th-center" style={{ width: 80 }}>Estado</th>
                <th className="mst-th mst-th-center" style={{ width: 80 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="mst-empty">Sin clientes registrados</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td className="mst-td">{r.tipo_documento}</td>
                  <td className="mst-td" style={{ fontFamily: 'monospace' }}>{r.nro_documento || '—'}</td>
                  <td className="mst-td" style={{ fontWeight: 600 }}>{r.nombre_completo || r.nombres}</td>
                  <td className="mst-td">{r.telefono || '—'}</td>
                  <td className="mst-td">{r.email || '—'}</td>
                  <td className="mst-td" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.direccion || '—'}
                  </td>
                  <td className="mst-td mst-td-center">
                    <span className={`mst-badge ${r.activo ? 'mst-badge-active' : 'mst-badge-inactive'}`}>
                      {r.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </td>
                  <td className="mst-td">
                    <div className="ia-actions">
                      <button className="ia-btn ia-btn--edit" title="Editar" onClick={() => { setConfirmDel(null); openEdit(r); }}>
                        <FiEdit2 size={15} />
                      </button>
                      <button
                        className={`ia-btn ${r.activo ? 'ia-btn--on' : 'ia-btn--off'}`}
                        title={r.activo ? 'Desactivar' : 'Activar'}
                        onClick={() => { setConfirmDel(null); handleToggle(r.id); }}
                      >
                        {r.activo ? <FiToggleLeft size={15} /> : <FiToggleRight size={15} />}
                      </button>
                      {confirmDel === r.id ? (
                        <button
                          className="ia-btn ia-btn--danger"
                          title="Confirmar eliminación"
                          onClick={e => { e.stopPropagation(); handleDelete(r.id); }}
                          style={{ background: '#fee2e2', color: '#dc2626', fontSize: 10, width: 'auto', padding: '0 6px', whiteSpace: 'nowrap' }}
                        >
                          ¿Eliminar?
                        </button>
                      ) : (
                        <button
                          className="ia-btn ia-btn--danger"
                          title="Eliminar cliente"
                          onClick={e => { e.stopPropagation(); handleDelete(r.id); }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mst-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* Modal */}
      {modal && (
        <div className="mst-overlay">
          <div className="mst-modal mst-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="mst-modal-header">
              <span>{modal.mode === 'new' ? 'Nuevo Cliente' : 'Editar Cliente'}</span>
              <button className="mst-modal-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div className="mst-modal-body">
              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Tipo Documento</label>
                  <select className="mst-select" value={form.tipo_documento}
                    onChange={e => handleChange('tipo_documento', e.target.value)}>
                    {tiposDoc.map(t => <option key={t.id} value={t.short_name}>{t.name}</option>)}
                    {!tiposDoc.some(t => t.short_name === form.tipo_documento) && (
                      <option value={form.tipo_documento}>{form.tipo_documento}</option>
                    )}
                  </select>
                </div>
                <div className="mst-field">
                  <label className="mst-label">N° Documento</label>
                  <input className="mst-input" value={form.nro_documento}
                    onChange={e => handleChange('nro_documento', e.target.value)}
                    placeholder={form.tipo_documento === 'DNI' ? '8 dígitos' : form.tipo_documento === 'RUC' ? '11 dígitos' : 'Nro. documento'}
                    maxLength={form.tipo_documento === 'DNI' ? 8 : form.tipo_documento === 'RUC' ? 11 : 20} />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Apellido Paterno</label>
                  <input className="mst-input" value={form.apellido_paterno}
                    onChange={e => handleChange('apellido_paterno', e.target.value)}
                    placeholder="Apellido paterno" />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Apellido Materno</label>
                  <input className="mst-input" value={form.apellido_materno}
                    onChange={e => handleChange('apellido_materno', e.target.value)}
                    placeholder="Apellido materno" />
                </div>
              </div>

              <div className="mst-row mst-row-1">
                <div className="mst-field">
                  <label className="mst-label">Nombres *</label>
                  <input className="mst-input" value={form.nombres}
                    onChange={e => handleChange('nombres', e.target.value)}
                    placeholder="Nombres" autoFocus />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Teléfono</label>
                  <input className="mst-input" value={form.telefono}
                    onChange={e => handleChange('telefono', e.target.value)}
                    placeholder="999 999 999" />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Email</label>
                  <input className="mst-input" type="email" value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="correo@ejemplo.com" />
                </div>
              </div>

              <div className="mst-row mst-row-1">
                <div className="mst-field">
                  <label className="mst-label">Dirección</label>
                  <input className="mst-input" value={form.direccion}
                    onChange={e => handleChange('direccion', e.target.value)}
                    placeholder="Dirección completa" />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">
                    Representante Legal
                    {form.tipo_documento !== 'RUC' && (
                      <span style={{ marginLeft: 6, fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>
                        (auto)
                      </span>
                    )}
                  </label>
                  <input
                    className="mst-input"
                    value={form.representante}
                    onChange={e => form.tipo_documento === 'RUC' && handleChange('representante', e.target.value)}
                    readOnly={form.tipo_documento !== 'RUC'}
                    style={form.tipo_documento !== 'RUC' ? { background: '#f1f5f9', color: '#64748b', cursor: 'default' } : {}}
                    placeholder={form.tipo_documento === 'RUC' ? 'Nombre del representante' : 'Se completa automáticamente'}
                  />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Límite de Crédito (S/)</label>
                  <input className="mst-input" type="number" min="0" step="0.01"
                    value={form.limite_credito}
                    onChange={e => handleChange('limite_credito', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>

            {modalError && <div className="mst-modal-error">{modalError}</div>}

            <div className="mst-modal-footer">
              <button className="mst-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Actualizar'}
              </button>
              <button className="mst-btn-cancel" onClick={() => setModal(null)}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
